import { Tile } from '../types/feedRef'
import {
  encodeTiles,
  decodeTiles,
  isCurrentFormat,
  DecodedState,
  saveSnapshotCache,
  loadSnapshotCache,
  hydrateSnapshots,
} from './persistence.v2'
import { migrateLegacyState } from './migration'

const STORAGE_KEY_PREFIX = 'multiview:'

export type { DecodedState }
export { saveSnapshotCache, loadSnapshotCache, hydrateSnapshots }

export const parsePersistedState = (raw: string | undefined): DecodedState => {
  if (!raw || raw.trim() === '') {
    return { tiles: [], colors: {} }
  }

  if (isCurrentFormat(raw)) {
    return decodeTiles(raw)
  }

  return migrateLegacyState(raw)
}

export const encodePersistedState = (
  tiles: Tile[],
  colors: Record<string, number>
): string => encodeTiles(tiles, colors)

export const saveToSessionStorageV2 = (
  orgId: string,
  tiles: Tile[],
  colors: Record<string, number>
): void => {
  const key = `${STORAGE_KEY_PREFIX}${orgId}`
  const value = encodePersistedState(tiles, colors)
  try {
    if (value) {
      sessionStorage.setItem(key, value)
    } else {
      sessionStorage.removeItem(key)
    }
  } catch {
    // sessionStorage may be unavailable (private mode, quota, or blocked by
    // policy). Persistence is best-effort — degrade silently.
  }
  saveSnapshotCache(orgId, tiles)
}

export const loadFromSessionStorageV2 = (orgId: string): DecodedState => {
  const key = `${STORAGE_KEY_PREFIX}${orgId}`
  let value: string | null = null
  try {
    value = sessionStorage.getItem(key)
  } catch {
    // Storage read denied/unavailable — fall back to empty persisted state so
    // route load (this runs in beforeLoad) does not break.
    value = null
  }
  const state = parsePersistedState(value ?? undefined)
  const cache = loadSnapshotCache(orgId)
  return {
    ...state,
    tiles: hydrateSnapshots(state.tiles, cache),
  }
}
